import { Box, Checkbox, FormControl, FormControlLabel, MenuItem, Select, Typography } from '@mui/material';
import { Move, isDoublePowerMoveId, isHakidasuMove, isHundonokobushiMove, isKakuteiCriticalMove, isKishikaiseiMove, isMagnitudeMove, isOhakamairiMove, isRenzokuMove, isStoredPowerMove } from '../../move/move';
import type { MoveDamageSetting } from '../../types/damage-setting';

// 状態異常（どく/状態異常）で威力2倍が自動判定できる技は、手動UIを出さない
// - 474: ベノムショック
// - 506: たたりめ
// - 839: どくばりセンボン
// - 844: ひゃっきやこう
const AUTO_DOUBLE_POWER_BY_STATUS_MOVE_IDS = new Set([474, 506, 839, 844]);

const shouldShowManualDoublePower = (moveId: number) =>
  isDoublePowerMoveId(moveId) && !AUTO_DOUBLE_POWER_BY_STATUS_MOVE_IDS.has(moveId);

const resolveCustomPowerOptions = (move: Move): { options: number[]; defaultValue: number } | null => {
  // きしかいせい / じたばた
  if (isKishikaiseiMove(move)) {
    return { options: [20, 40, 80, 100, 150, 200], defaultValue: 20 };
  }
  // マグニチュード
  if (isMagnitudeMove(move)) {
    return { options: [10, 30, 50, 70, 90, 110, 150], defaultValue: 10 };
  }
  // おはかまいり / ふんどのこぶし
  if (isOhakamairiMove(move) || isHundonokobushiMove(move)) {
    return { options: [50, 100, 150, 200, 250, 300, 350], defaultValue: 50 };
  }
  // アシストパワー / つけあがる
  if (isStoredPowerMove(move)) {
    return { options: Array.from({ length: 43 }, (_, index) => 20 + index * 20), defaultValue: 20 };
  }
  // はきだす
  if (isHakidasuMove(move)) {
    return { options: [100, 200, 300], defaultValue: 100 };
  }
  return null;
};

const normalizeCriticalSetting = (move: Move, setting: MoveDamageSetting) => {
  // 確定急所は入力を出さないが、内部値はそのままにしておく（計算側で確定急所が優先される）
  if (isKakuteiCriticalMove(move)) {
    return setting;
  }
  if (isRenzokuMove(move)) {
    const nextCount = Math.max(0, Math.floor(setting.criticalHitCount ?? 0));
    return {
      ...setting,
      isCritical: nextCount > 0,
      criticalHitCount: nextCount,
    };
  }
  const checked = Boolean(setting.isCritical);
  return {
    ...setting,
    isCritical: checked,
    criticalHitCount: checked ? 1 : 0,
  };
};

export type DamageSettingSummaryTag = {
  key: string;
  label: string;
};

export const resolveDamageSettingVisibility = (
  move: Move,
  showJuuden: boolean,
) => {
  const showCritical = !isKakuteiCriticalMove(move);
  const showDoublePower = shouldShowManualDoublePower(move.id);
  const hasCustomPower = Boolean(resolveCustomPowerOptions(move));
  return {
    showCritical,
    showDoublePower,
    showJuuden,
    hasCustomPower,
    hasAnyControl: showCritical || showDoublePower || showJuuden || hasCustomPower,
  };
};

export const buildDamageSettingSummaryTags = (
  move: Move,
  setting: MoveDamageSetting,
  showJuuden: boolean,
): DamageSettingSummaryTag[] => {
  const tags: DamageSettingSummaryTag[] = [];
  const normalized = resolveMoveDamageSettingWithDefaults(move, setting);
  const { showCritical, showDoublePower, hasCustomPower } = resolveDamageSettingVisibility(move, showJuuden);

  if (showCritical) {
    if (isRenzokuMove(move)) {
      const hitCount = normalized.criticalHitCount ?? 0;
      if (hitCount > 0) {
        tags.push({ key: 'critical', label: `急所x${hitCount}` });
      }
    } else if (normalized.isCritical) {
      tags.push({ key: 'critical', label: '急所' });
    }
  }

  if (showDoublePower && normalized.isDoublePower) {
    tags.push({ key: 'double', label: '2倍' });
  }

  if (showJuuden && normalized.isJuuden) {
    tags.push({ key: 'juuden', label: 'じゅうでん' });
  }

  if (hasCustomPower) {
    const customPower = normalized.customPower ?? resolveCustomPowerOptions(move)?.defaultValue ?? 0;
    if (customPower > 0) {
      tags.push({ key: 'power', label: `威力:${customPower}` });
    }
  }

  return tags;
};

export function resolveMoveDamageSettingWithDefaults(move: Move, raw: MoveDamageSetting | undefined): MoveDamageSetting {
  const base = raw ?? {};
  const customPowerMeta = resolveCustomPowerOptions(move);
  const nextCustomPower =
    customPowerMeta && (base.customPower == null || base.customPower === 0)
      ? customPowerMeta.defaultValue
      : base.customPower;

  const criticalNormalized = normalizeCriticalSetting(move, base);

  return {
    ...criticalNormalized,
    customPower: nextCustomPower,
    // 状態異常で自動2倍ができる技は、手動2倍を無効化する（UI表示もしない）
    isDoublePower: shouldShowManualDoublePower(move.id) ? Boolean(base.isDoublePower) : false,
  };
}

export function DamageSettingHeaderControls({
  move,
  setting,
  onChangeSetting,
  showJuuden,
}: {
  move: Move;
  setting: MoveDamageSetting;
  onChangeSetting: (next: MoveDamageSetting) => void;
  showJuuden: boolean;
}) {
  const normalized = normalizeCriticalSetting(move, setting);
  const showDoublePower = shouldShowManualDoublePower(move.id);
  const showCritical = !isKakuteiCriticalMove(move);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-start' }}>
      {showCritical ? (
        isRenzokuMove(move) ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
              急所
            </Typography>
            <FormControl size="small" sx={{ minWidth: 64 }}>
              <Select
                value={normalized.criticalHitCount ?? 0}
                onChange={(e) => {
                  const criticalHitCount = Number(e.target.value);
                  onChangeSetting(
                    normalizeCriticalSetting(move, {
                      ...setting,
                      criticalHitCount,
                    }),
                  );
                }}
                sx={{
                  fontSize: '0.7rem',
                  '& .MuiSelect-select': {
                    padding: '2px 6px',
                    textAlign: 'center',
                  },
                }}
              >
                {Array.from({ length: (move.max_renzoku || 5) + 1 }, (_, hitCount) => (
                  <MenuItem key={hitCount} value={hitCount} sx={{ fontSize: '0.7rem', justifyContent: 'center' }}>
                    {hitCount}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        ) : (
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={Boolean(normalized.isCritical)}
                onChange={(e) => onChangeSetting(normalizeCriticalSetting(move, { ...setting, isCritical: e.target.checked }))}
              />
            }
            label="急所"
            labelPlacement="start"
            sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.7rem' } }}
          />
        )
      ) : null}

      {showJuuden ? (
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={Boolean(setting.isJuuden)}
              onChange={(e) => onChangeSetting({ ...setting, isJuuden: e.target.checked })}
            />
          }
          label="じゅうでん"
          labelPlacement="start"
          sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.7rem' } }}
        />
      ) : null}

      {showDoublePower ? (
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={Boolean(setting.isDoublePower)}
              onChange={(e) => onChangeSetting({ ...setting, isDoublePower: e.target.checked })}
            />
          }
          label="2倍"
          labelPlacement="start"
          sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.7rem' } }}
        />
      ) : null}
    </Box>
  );
}

export function DamageSettingPowerSelect({
  move,
  setting,
  onChangeSetting,
}: {
  move: Move;
  setting: MoveDamageSetting;
  onChangeSetting: (next: MoveDamageSetting) => void;
}) {
  const customPowerMeta = resolveCustomPowerOptions(move);
  if (!customPowerMeta) {
    return null;
  }

  const value = setting.customPower ?? customPowerMeta.defaultValue;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Typography variant="caption" sx={{ fontSize: '0.7rem', whiteSpace: 'nowrap' }}>
        威力
      </Typography>
      <FormControl size="small" sx={{ minWidth: 88 }}>
        <Select
          value={value}
          onChange={(e) => {
            const next = Number(e.target.value);
            onChangeSetting({ ...setting, customPower: next });
          }}
          sx={{
            fontSize: '0.7rem',
            '& .MuiSelect-select': {
              padding: '2px 6px',
              textAlign: 'center',
            },
          }}
        >
          {customPowerMeta.options.map((power) => (
            <MenuItem key={power} value={power} sx={{ fontSize: '0.7rem', justifyContent: 'center' }}>
              {power}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
}
